/**
 * Craft menu — the crafting-station counterpart to the Z build menu. Rebuilt to
 * mirror z-menu.ts's hud-v10 shelf (docs/design/mockups/hud-v10-build-menu.html):
 * a gold-rim panel that sits just above the lower-HUD stack, slot-grouped tabs,
 * preview-icon tiles, resource-icon cost chips, affordability dimming, and a
 * hover tooltip. Reuses the `.build-menu` / `.bm-*` CSS so it reads identically
 * to the build menu; only `.craft-shelf` adds a couple of craft-specific tweaks.
 *
 * Serves all three crafting stations (Forge / Workbench / Campfire). Opened by
 * the E-key near a station, or by left-clicking a Forge/Workbench directly
 * (those skip the building action menu entirely — Salvage now lives in this
 * shelf's footer). The menu filters RECIPES by buildingType and groups the
 * survivors by item slot into tabs (Weapons / Offhand / Armor for Forge &
 * Workbench; Food / Utility for Campfire).
 *
 * Controls (wired in main.ts, mirroring the Z menu): E toggles · Tab switches
 * slot tab · number keys 1-9 craft the nth tile in the active tab.
 *
 * Open context (CraftCtx) carries the building id (for Salvage), owner flag,
 * and world position (for the "move closer" proximity pre-flight). The hover
 * tooltip (name · slot · stats · description · spelled-out cost) is a single
 * body-level panel, not a per-tile child, so the scrollable grid can't clip it.
 *
 * Affordability (per Phase 7 D5, preserved): recipes with `unknown`-keyed costs
 * (survival/quest deps the server treats as unsatisfiable) are dropped at
 * render-time. Resource shortfalls dim the tile + redden the short chip; the
 * tile stays clickable so the server's "silent no-op on insufficient" stays the
 * source of truth, and a pre-flight toast tells the player what's missing.
 * Item-as-ingredient (KTS upgrade-chain) costs are shown but not used to dim.
 */
import {
  ITEMS, RECIPES, RESOURCE_KEYS, getMaterial,
  type BuildingType, type Cube, type ItemSlot, type ItemDef, type RecipeDef,
} from "@tribez/shared";
import { buildPetMasteryRows, buildTrophyRows } from "../hud/pet-mastery-panel";
import { hasRealItemIcon, itemIconUrl } from "./item-icons";

/** Station context for an open menu. Drives the proximity pre-flight + the
 *  Salvage footer (owner-only). All optional so E-key / building-menu callers
 *  can open without it (proximity then falls back to the server's check). */
export interface CraftCtx {
  buildingId?: string;
  isOwner?: boolean;
  /** Building world position — used for the "move closer" pre-flight. */
  bx?: number;
  bz?: number;
}

export interface CraftMenu {
  open(buildingType: BuildingType, ctx?: CraftCtx): void;
  close(): void;
  isOpen(): boolean;
  /** Recompute affordability dimming. Call each frame while open. */
  refresh(cube: Cube): void;
  /** Switch to the next/previous slot tab (Tab / Shift+Tab). */
  cycleTab(dir?: 1 | -1): void;
  /** Craft the nth (1-based) tile in the active tab (number-row keys). */
  selectHotkey(n: number): void;
}

type SendFn = (type: string, payload: unknown) => void;
type NotifyFn = (msg: string) => void;
type CostKind = "resource" | "item" | "unknown";

/** Strip KTS tooltip markup to the item's flavor prose. The KTS `tooltip`
 *  string is flavor text followed by a `<s val="TechDescription">` stat block
 *  (which we already surface via statSummary). We keep only the flavor, drop
 *  unresolved `<d ref="…"/>` dynamic values, and strip the remaining tags
 *  (`<n/>`, `<c>`, …). Empty result → no description line. */
function itemDescription(def: ItemDef): string {
  const raw = def.tooltip;
  if (!raw) return "";
  const flavor = raw.split(/<s\b[^>]*>/i)[0] ?? raw;
  return flavor
    .replace(/<d\b[^>]*\/?>/gi, "")
    .replace(/<\/?[a-z][^>]*>/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Cost-key classification — mirror of server's craft.ts classifyCostKey,
 *  reimplemented here so we don't import server code. */
function classifyCostKey(key: string): CostKind {
  if ((RESOURCE_KEYS as ReadonlyArray<string>).includes(key)) return "resource";
  if (ITEMS[key] !== undefined) return "item";
  return "unknown";
}

// --- Expertise gate (mirror of server craft.ts) ------------------------------
// The server gates a craft on the player's Artisanship/Cooking expertise: each
// recipe carries `artisanshipReq`/`cookingReq` in KTS skill-units (0-25), and a
// cube satisfies it when `tier × 5 ≥ req` (1 v1 tier = 5 KTS levels). We replicate
// getExpertiseLevel here (server/src/expertise.ts) off the synced `cube.expertise`
// sub-schema so the menu can show what's actually craftable NOW, not just
// affordable. Until this fix the menu ignored the gate entirely.
const KTS_LEVELS_PER_TIER = 5;
type GateTree = "Artisanship" | "Cooking";
/** The gate that applies to a recipe at its station (Campfire → Cooking, else Artisanship). */
function gateFor(recipe: RecipeDef, st: BuildingType | null): { req: number; tree: GateTree } {
  return st === "Campfire"
    ? { req: recipe.cookingReq, tree: "Cooking" }
    : { req: recipe.artisanshipReq, tree: "Artisanship" };
}
/** Player's KTS skill-units in a tree (tier × 5). Reads the synced expertise schema. */
function playerGateUnits(cube: Cube, tree: GateTree): number {
  const field = tree === "Cooking" ? "cooking" : "artisanship";
  const tier = (cube.expertise as unknown as Record<string, number | undefined>)[field] ?? 0;
  return tier * KTS_LEVELS_PER_TIER;
}
/** v1 tier required to unlock a recipe (KTS units → tier, rounding up). */
function gateTier(req: number): number { return Math.ceil(req / KTS_LEVELS_PER_TIER); }
/** Count of an item id held across the cube's backpack stacks. */
function backpackCount(cube: Cube, itemId: string): number {
  let n = 0;
  cube.backpack.forEach((s) => { if (s.itemId === itemId) n += s.count; });
  return n;
}

// --- Tab grouping ------------------------------------------------------------
// Recipes are grouped by the output item's slot. Tab order is fixed; only slots
// with ≥1 surviving recipe render a tab. Labels depend slightly on the station
// (a Campfire's "consumable" is Food; a Forge's lone consumable is Consumable).
const SLOT_ORDER: ItemSlot[] = ["main-hand", "offhand", "armor", "consumable", "utility"];
function slotLabel(slot: ItemSlot, station: BuildingType): string {
  switch (slot) {
    case "main-hand":  return "Weapons";
    case "offhand":    return "Offhand";
    case "armor":      return "Armor";
    case "consumable": return station === "Campfire" ? "Food" : "Consumable";
    case "utility":    return "Utility";
  }
}

// --- Icon resolver -----------------------------------------------------------
// Bespoke PNG art (shared with the inventory via item-icons.ts) for any item
// that has it; the per-slot SVG glyphs below are the fallback for the art-less
// long tail. Single source of truth: REAL_ICON_IDS in item-icons.ts.
const SLOT_ICON_SVG: Record<ItemSlot, string> = {
  "main-hand": `<svg viewBox="0 0 62 62"><path d="M44 10 L52 18 L26 44 L18 44 L18 36 Z" fill="#c8d2de" stroke="#5a6470" stroke-width="2"/><path d="M16 38 L24 46 L18 52 L12 46 Z" fill="#8b6a3d" stroke="#4a2818" stroke-width="2"/><path d="M14 48 L10 52" stroke="#4a2818" stroke-width="3" stroke-linecap="round"/></svg>`,
  "offhand":   `<svg viewBox="0 0 62 62"><path d="M31 8 L50 16 L50 34 Q50 50 31 56 Q12 50 12 34 L12 16 Z" fill="#7a5630" stroke="#3a2010" stroke-width="2"/><path d="M31 8 L31 56 M12 30 L50 30" stroke="#caa84e" stroke-width="2"/><circle cx="31" cy="30" r="5" fill="#caa84e"/></svg>`,
  "armor":     `<svg viewBox="0 0 62 62"><path d="M20 10 L31 16 L42 10 L50 18 L46 50 Q31 58 16 50 L12 18 Z" fill="#8a8578" stroke="#3a3530" stroke-width="2"/><path d="M31 16 L31 54 M20 24 L42 24" stroke="#5a5048" stroke-width="2"/></svg>`,
  "consumable":`<svg viewBox="0 0 62 62"><path d="M40 14 Q52 18 50 34 Q48 50 34 52 L30 52 Q16 50 14 34 Q12 18 24 14 Z" fill="#c46a3a" stroke="#6a3418" stroke-width="2"/><path d="M30 14 L26 6 M34 14 L40 7" stroke="#8b6a3d" stroke-width="3" stroke-linecap="round"/><ellipse cx="28" cy="30" rx="5" ry="7" fill="#e8a060" opacity="0.7"/></svg>`,
  "utility":   `<svg viewBox="0 0 62 62"><path d="M22 22 Q22 14 31 14 Q40 14 40 22 L46 26 Q52 44 44 52 L18 52 Q10 44 16 26 Z" fill="#8b6a3d" stroke="#4a2818" stroke-width="2"/><path d="M24 22 L38 22" stroke="#4a2818" stroke-width="2"/><circle cx="31" cy="38" r="6" fill="#caa84e"/></svg>`,
};
function iconFor(def: ItemDef): string {
  if (hasRealItemIcon(def.id))
    return `<img class="bm-tile__img" src="${itemIconUrl(def.id)}" alt="" draggable="false">`;
  return SLOT_ICON_SVG[def.slot] ?? "";
}

// --- Cost chips --------------------------------------------------------------
// Colour-keyed resource glyphs (13×13). Resources without a bespoke glyph fall
// back to a neutral dot; item-ingredient costs use a generic crate glyph (the
// full ingredient name lives in the tooltip's cost breakdown).
const RES_ICON: Record<string, string> = {
  lumber:        '<rect x="1" y="4" width="11" height="7" rx="1" fill="#8b6a3d"/>',
  infusedLumber: '<rect x="1" y="4" width="11" height="7" rx="1" fill="#6abf6a"/>',
  stone:         '<path d="M2 10 L5 3 L9 3 L11 10 Z" fill="#8a8578"/>',
  ironOre:       '<path d="M6.5 1 L12 6.5 L6.5 12 L1 6.5 Z" fill="#c9d4e0"/>',
  wool:          '<circle cx="6.5" cy="6.5" r="5" fill="#e6e0d2"/>',
  leather:       '<path d="M3 3 L10 3 L12 6 L10 11 L3 11 L1 6 Z" fill="#8b5a2b"/>',
  gold:          '<circle cx="6.5" cy="6.5" r="5" fill="#e8c14e"/>',
  berry:         '<circle cx="6.5" cy="7" r="4.5" fill="#c0392b"/>',
  herb:          '<path d="M6.5 12 Q1 7 6.5 1 Q12 7 6.5 12 Z" fill="#4e9e4e"/>',
  sageberry:     '<circle cx="6.5" cy="7" r="4.5" fill="#8e44ad"/>',
  spicyHerbs:    '<path d="M6.5 12 Q1 7 6.5 1 Q12 7 6.5 12 Z" fill="#d35400"/>',
  shadowstone:   '<path d="M6.5 1 L11 5 L9 12 L4 12 L2 5 Z" fill="#5b3a8a"/>',
  radiantGem:    '<path d="M6.5 1 L11 5 L9 12 L4 12 L2 5 Z" fill="#3ec8d0"/>',
  bearPelt:      '<path d="M3 3 L10 3 L12 6 L10 11 L3 11 L1 6 Z" fill="#5a3a20"/>',
  ancientArtifactFragment: '<path d="M2 11 L4 3 L9 3 L11 11 Z" fill="#caa84e"/>',
};
const RES_FALLBACK = '<circle cx="6.5" cy="6.5" r="4.5" fill="#8a96a8"/>';
const ITEM_CHIP = '<rect x="2" y="3" width="9" height="8" rx="1" fill="#9a7a4a" stroke="#5a4020"/>';

/** Human label for a cost key (tooltip breakdown). */
function costLabel(key: string, kind: CostKind): string {
  if (kind === "item") return ITEMS[key]?.displayName ?? key;
  // camelCase resource → "Title Case" (ironOre → Iron Ore).
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).trim();
}

interface CostEntry { key: string; amount: number; kind: CostKind; }
function costEntries(def: ItemDef): CostEntry[] {
  return Object.entries(def.craftingCost).map(([key, amount]) => ({
    key, amount: Number(amount), kind: classifyCostKey(key),
  }));
}

// --- Stat summary (tooltip) --------------------------------------------------
// A compact "STR +3 · Armor +5" line drawn from the item's non-zero stats, so
// the tooltip carries real decision info beyond the name + cost.
const STAT_LABEL: Record<string, string> = {
  strength: "STR", dexterity: "DEX", endurance: "END", intelligence: "INT",
  willpower: "WIL", focus: "FOC", physique: "Physique", wit: "Wit",
  armor: "Armor", sArmor: "S.Armor", armorPen: "Pen", manaMax: "Mana",
  hpMax: "HP", critChance: "Crit", critDamage: "Crit Dmg", attackRange: "Range",
};
function statSummary(def: ItemDef): string {
  const out: string[] = [];
  for (const [k, label] of Object.entries(STAT_LABEL)) {
    const v = (def.stats as Record<string, number | undefined>)[k];
    if (v != null && v !== 0) out.push(`${label} ${v > 0 ? "+" : ""}${v}`);
    if (out.length >= 4) break;
  }
  return out.join(" · ");
}

// DOM refs cached per tile so refresh() mutates classes in place (re-rendering
// innerHTML every frame detaches the element a click is resolving and the click
// misses — same gotcha the build/building menus document).
interface TileRef { recipe: RecipeDef; def: ItemDef; el: HTMLDivElement; costEls: HTMLSpanElement[]; entries: CostEntry[]; gateReq: number; gateTree: GateTree; }

// 5-column grid (.craft-shelf .bm-main) × 2 rows = one page. Paginating instead
// of scrolling keeps the shelf to a fixed two-row height (Aviv 2026-06-30).
const TILES_PER_PAGE = 10;

/** A recipe tab groups recipes by item slot (existing behaviour). */
interface RecipeTabDef { kind: "recipe"; slot: ItemSlot; label: string; recipes: RecipeDef[]; }

/** A custom tab renders arbitrary content (Pet Mastery, Trophies). */
interface CustomTabDef {
  kind: "custom";
  label: string;
  /** Called once when the tab first becomes active; appends content into `container`. */
  render(container: HTMLElement): void;
  /** Called each refresh() while this tab is active. */
  refresh(cube: Cube): void;
}

type TabDef = RecipeTabDef | CustomTabDef;

export function createCraftMenu(send: SendFn, notify?: NotifyFn): CraftMenu {
  const root = document.createElement("div");
  root.className = "build-menu craft-shelf";
  root.style.display = "none";
  document.body.appendChild(root);

  let opened = false;
  let station: BuildingType | null = null;
  let ctx: CraftCtx = {};
  let tabs: TabDef[] = [];
  let activeTab = 0;
  let page = 0;                       // current page within the active recipe tab
  let pageRecipes: RecipeDef[] = [];  // the recipes shown on the current page (hotkey targets)
  let tiles: TileRef[] = [];
  let lastCube: Cube | null = null;
  // Active custom tab refresh fn — set when a custom tab is rendered.
  let activeCustomRefresh: ((cube: Cube) => void) | null = null;

  // Live tab-strip / grid nodes — reassigned to the freshly-rendered children
  // each open() (root.innerHTML is rebuilt per open). renderTab()/affordability
  // close over these, so they always point at the in-DOM nodes.
  let tabsEl: HTMLDivElement = document.createElement("div");
  let mainEl: HTMLDivElement = document.createElement("div");
  let pagerEl: HTMLDivElement = document.createElement("div");

  // Tooltip lives on <body>, not inside a tile: the tile grid is a scroll
  // container (overflow-y:auto), so an in-grid absolutely-positioned tooltip got
  // clipped and a right-edge one forced a horizontal scrollbar. A single fixed
  // panel positioned per-hover sidesteps both and clamps to the viewport.
  const tip = document.createElement("div");
  tip.className = "craft-tip";
  tip.style.display = "none";
  document.body.appendChild(tip);

  function hideTip(): void { tip.style.display = "none"; }
  function showTip(ref: TileRef, tabLabel: string): void {
    const def = ref.def;
    const stats = statSummary(def);
    const desc = itemDescription(def);
    // Cost rows carry the same material glyph as the tile chips, so the icon
    // reads next to the spelled-out name + amount.
    const costRows = ref.entries.length
      ? ref.entries.map((c) => {
          const ic = c.kind === "item" ? ITEM_CHIP : (RES_ICON[c.key] ?? RES_FALLBACK);
          return `<div class="craft-tip__ci"><svg viewBox="0 0 13 13">${ic}</svg><span>${c.amount}× ${costLabel(c.key, c.kind)}</span></div>`;
        }).join("")
      : `<div class="craft-tip__ci"><span>—</span></div>`;
    const cook = station === "Campfire" && ref.recipe.prepSec > 0 ? ` · ${ref.recipe.prepSec}s cook` : "";
    tip.innerHTML = `
      <div class="bm-tip__title">${def.displayName}</div>
      <div class="bm-tip__meta">${tabLabel}${cook}</div>
      ${stats ? `<div class="bm-tip__desc">${stats}</div>` : ""}
      ${desc ? `<div class="bm-tip__flavor">${desc}</div>` : ""}
      <div class="bm-tip__cost"><div class="craft-tip__cost-hd">Cost</div>${costRows}</div>`;
    tip.style.display = "block";
    positionTip(ref.el);
  }
  const TIP_GAP = 10, TIP_EDGE = 8;
  function positionTip(tileEl: HTMLElement): void {
    const r = tileEl.getBoundingClientRect();
    const w = tip.offsetWidth, h = tip.offsetHeight;
    let x = r.left + r.width / 2 - w / 2;
    x = Math.max(TIP_EDGE, Math.min(x, window.innerWidth - w - TIP_EDGE));
    let y = r.top - h - TIP_GAP;                 // prefer above the tile
    if (y < TIP_EDGE) y = r.bottom + TIP_GAP;    // flip below if it would clip the top
    tip.style.left = `${Math.round(x)}px`;
    tip.style.top = `${Math.round(y)}px`;
  }

  // Group this station's surviving recipes into slot tabs. Recipes with any
  // `unknown` cost key are dropped (server treats them as unsatisfiable).
  // For HuntersLodge, also appends two custom tabs (Pet Mastery, Trophies).
  function buildTabs(buildingType: BuildingType): TabDef[] {
    const bySlot = new Map<ItemSlot, RecipeDef[]>();
    for (const recipe of Object.values(RECIPES)) {
      if (recipe.buildingType !== buildingType) continue;
      const def = ITEMS[recipe.outputItemId];
      if (!def) continue;
      if (Object.keys(def.craftingCost).some((k) => classifyCostKey(k) === "unknown")) continue;
      const arr = bySlot.get(def.slot) ?? [];
      arr.push(recipe);
      bySlot.set(def.slot, arr);
    }
    const out: TabDef[] = [];
    for (const slot of SLOT_ORDER) {
      const recipes = bySlot.get(slot);
      if (!recipes || recipes.length === 0) continue;
      // Sort by Artisanship/Cooking requirement ascending (so the recipes a
      // player is most likely able to craft surface first), then by name.
      recipes.sort((a, b) => {
        const ar = gateFor(a, buildingType).req;
        const br = gateFor(b, buildingType).req;
        if (ar !== br) return ar - br;
        const an = ITEMS[a.outputItemId]?.displayName ?? a.outputItemId;
        const bn = ITEMS[b.outputItemId]?.displayName ?? b.outputItemId;
        return an.localeCompare(bn);
      });
      out.push({ kind: "recipe", slot, label: slotLabel(slot, buildingType), recipes });
    }

    // For the Hunter's Lodge, append custom tabs for mastery + trophies.
    if (buildingType === "HuntersLodge") {
      const masteryBuilder = buildPetMasteryRows(send, notify);
      const trophyBuilder = buildTrophyRows(send, notify);
      out.push({
        kind: "custom",
        label: "Pet Mastery",
        render(container: HTMLElement): void {
          container.appendChild(masteryBuilder.el);
        },
        refresh(cube: Cube): void { masteryBuilder.refresh(cube); },
      });
      out.push({
        kind: "custom",
        label: "Trophies",
        render(container: HTMLElement): void {
          container.appendChild(trophyBuilder.el);
        },
        refresh(cube: Cube): void { trophyBuilder.refresh(cube); },
      });
    }

    return out;
  }

  function pick(recipe: RecipeDef): void {
    const def = ITEMS[recipe.outputItemId];
    const name = def?.displayName ?? recipe.outputItemId;
    // Proximity pre-flight: the menu can be opened by clicking a station up to
    // 6u away, but the server only crafts within INTERACT_RADIUS. Tell the
    // player to move closer instead of letting the craft silently no-op.
    if (lastCube && ctx.bx != null && ctx.bz != null) {
      const d = Math.hypot(ctx.bx - lastCube.x, ctx.bz - lastCube.z);
      if (d > INTERACT_RADIUS) { notify?.(`Move closer to the ${station ?? "station"}`); return; }
    }
    // Pre-flight: tell the player WHY a craft will fail before the server
    // silently no-ops. Server stays authoritative; this is pure UX feedback.
    if (lastCube) {
      // Expertise gate first — a locked recipe can't be crafted at any cost.
      const g = gateFor(recipe, station);
      if (g.req > 0 && playerGateUnits(lastCube, g.tree) < g.req) {
        notify?.(`Requires ${g.tree} tier ${gateTier(g.req)}`); return;
      }
    }
    if (lastCube && def) {
      const shortfalls: string[] = [];
      for (const c of costEntries(def)) {
        if (c.kind === "resource") {
          const have = getMaterial(lastCube, c.key);
          if (have < c.amount) shortfalls.push(`${c.amount - have} more ${costLabel(c.key, c.kind)}`);
        } else if (c.kind === "item") {
          let have = 0;
          lastCube.backpack.forEach((slot) => { if (slot.itemId === c.key) have += slot.count; });
          if (have < c.amount) shortfalls.push(`${c.amount - have} more ${costLabel(c.key, c.kind)}`);
        }
      }
      if (shortfalls.length > 0) { notify?.(`Can't craft ${name}: need ${shortfalls.join(", ")}`); return; }
    }
    send("craft", { recipeId: recipe.id });
    notify?.(`${station === "Campfire" ? "Cooking" : "Crafting"}: ${name}`);
  }

  function renderTab(): void {
    const tab = tabs[activeTab];
    hideTip();
    Array.from(tabsEl.children).forEach((b, i) => b.classList.toggle("bm-tab--active", i === activeTab));
    mainEl.innerHTML = "";
    pagerEl.innerHTML = "";
    pagerEl.style.display = "none";
    tiles = [];
    pageRecipes = [];
    activeCustomRefresh = null;
    if (!tab) return;

    if (tab.kind === "custom") {
      // Custom tab: delegate rendering to the tab's render() function.
      tab.render(mainEl);
      activeCustomRefresh = tab.refresh;
      // Apply an initial refresh if we have a cube.
      if (lastCube) tab.refresh(lastCube);
      return;
    }

    // Recipe tab: paginate to one 2-row page, then render that page's tiles.
    const pageCount = Math.max(1, Math.ceil(tab.recipes.length / TILES_PER_PAGE));
    page = Math.max(0, Math.min(page, pageCount - 1));
    const start = page * TILES_PER_PAGE;
    pageRecipes = tab.recipes.slice(start, start + TILES_PER_PAGE);

    pageRecipes.forEach((recipe, idx) => {
      const def = ITEMS[recipe.outputItemId]!;
      const entries = costEntries(def);
      const { req, tree } = gateFor(recipe, station);
      const tile = document.createElement("div");
      tile.className = "bm-tile";
      const chips = entries
        .map((c) => `<span class="bm-cost"><svg viewBox="0 0 13 13">${
          c.kind === "item" ? ITEM_CHIP : (RES_ICON[c.key] ?? RES_FALLBACK)
        }</svg>${c.amount}</span>`)
        .join("");
      // Lock badge (hidden unless the tile gets .bm-tile--locked) names the tier
      // the player needs; applyAffordability decides whether it shows.
      const lock = req > 0 ? `<span class="bm-tile__lock">🔒 ${tree} ${gateTier(req)}</span>` : "";
      tile.innerHTML = `
        ${idx < 9 ? `<span class="bm-tile__hk">${idx + 1}</span>` : ""}
        <div class="bm-tile__preview">${iconFor(def)}</div>
        <div class="bm-tile__name">${def.displayName}</div>
        <div class="bm-cost-row">${chips}</div>
        ${lock}`;
      tile.addEventListener("click", () => pick(recipe));
      mainEl.appendChild(tile);
      const costEls = Array.from(tile.querySelectorAll<HTMLSpanElement>(".bm-cost"));
      const ref: TileRef = { recipe, def, el: tile, costEls, entries, gateReq: req, gateTree: tree };
      tile.addEventListener("mouseenter", () => showTip(ref, tab.label));
      tile.addEventListener("mouseleave", hideTip);
      tiles.push(ref);
    });
    renderPager(pageCount);
    applyAffordability();
  }

  // Pager strip below the grid — only shown when a tab spans >1 page. Prev/Next
  // re-render the active tab at the new page; the number-row hotkeys + Tab still
  // operate on the visible page.
  function renderPager(pageCount: number): void {
    if (pageCount <= 1) { pagerEl.innerHTML = ""; pagerEl.style.display = "none"; return; }
    pagerEl.style.display = "flex";
    pagerEl.innerHTML = `
      <button class="craft-pager__btn" data-dir="-1" type="button" ${page === 0 ? "disabled" : ""}>‹</button>
      <span class="craft-pager__lbl">Page ${page + 1} / ${pageCount}</span>
      <button class="craft-pager__btn" data-dir="1" type="button" ${page === pageCount - 1 ? "disabled" : ""}>›</button>`;
    pagerEl.querySelectorAll<HTMLButtonElement>(".craft-pager__btn").forEach((b) => {
      b.addEventListener("click", () => {
        const dir = Number(b.dataset.dir);
        page = Math.max(0, Math.min(page + dir, pageCount - 1));
        renderTab();
      });
    });
  }

  // Classify every visible tile into one of three reads (Aviv 2026-06-30 — the
  // menu must only highlight what's craftable NOW):
  //   • locked     — expertise gate unmet (greyed + the 🔒 tier badge shows)
  //   • unaffordable — gate met but a material/ingredient is short (dimmed + red chip)
  //   • ready      — gate met and everything affordable (accented as actionable)
  // Unlike the old D5 behaviour, item-ingredient shortfalls now count against
  // affordability too, so a highlighted tile is one the server will actually craft.
  function applyAffordability(): void {
    for (const t of tiles) {
      let short = false;
      t.entries.forEach((c, i) => {
        let s = false;
        if (c.kind === "resource") s = !lastCube || getMaterial(lastCube, c.key) < c.amount;
        else if (c.kind === "item") s = !lastCube || backpackCount(lastCube, c.key) < c.amount;
        t.costEls[i]?.classList.toggle("bm-cost--short", s);
        if (s) short = true;
      });
      const locked = t.gateReq > 0 && (!lastCube || playerGateUnits(lastCube, t.gateTree) < t.gateReq);
      t.el.classList.toggle("bm-tile--locked", locked);
      t.el.classList.toggle("bm-tile--unaffordable", !locked && short);
      t.el.classList.toggle("bm-tile--ready", !locked && !short);
    }
  }

  function setTab(i: number): void {
    if (tabs.length === 0) return;
    activeTab = (i + tabs.length) % tabs.length;
    page = 0;                 // a fresh tab always starts on its first page
    renderTab();
  }
  function cycleTab(dir: 1 | -1 = 1): void { setTab(activeTab + dir); }
  function selectHotkey(n: number): void {
    // Hotkeys only apply to recipe tabs; custom tabs (Pet Mastery, Trophies) are
    // click-driven. The keys map to the nth tile on the VISIBLE page.
    const tab = tabs[activeTab];
    if (!tab || tab.kind !== "recipe") return;
    const recipe = pageRecipes[n - 1];
    if (recipe) pick(recipe);
  }

  // Sit the panel just above the lower-HUD stack — same anchor logic as the
  // build menu so the two shelves occupy the same spot. Unlike the build menu
  // (≤4 tiles), a station tab can hold 12-16 recipes, so the bottom-anchored
  // panel would grow up off the top of the screen and collide with the
  // top-centre day/night clock. Cap the height to the gap between the clock
  // band (~TOP_RESERVE) and the HUD, and let the tile grid scroll inside it.
  const TOP_RESERVE = 110;
  function positionAboveHud(): void {
    const ref = document.querySelector(".exp-arc") ?? document.getElementById("main-plate");
    if (!ref) return;
    const r = ref.getBoundingClientRect();
    if (r.height === 0) return;
    root.style.bottom = `${Math.round(window.innerHeight - r.top + 10)}px`;
    const avail = Math.round(r.top - 10 - TOP_RESERVE);
    root.style.maxHeight = `${Math.max(240, avail)}px`;
  }

  /** Human-readable display name for each building type. */
  function stationDisplayName(bt: BuildingType): string {
    if (bt === "HuntersLodge") return "Hunter's Lodge";
    return bt;
  }

  function render(): void {
    const stationName = stationDisplayName(station ?? "Forge");
    const verb = station === "Campfire" ? "Cook" : "Craft";
    // Salvage moved here from the building action menu — owner-only, and only
    // when we know which building opened the shelf.
    const salvage = ctx.isOwner && ctx.buildingId
      ? `<div class="craft-foot"><button class="craft-salvage" type="button">⚒ Salvage ${stationName} · 50% refund</button></div>`
      : "";
    root.innerHTML = `
      <div class="bm-header">
        <div class="bm-title">✧ ${verb} · ${stationName} ✧</div>
        <div class="bm-close"><span class="bm-key">Tab</span> switch · <span class="bm-key">E</span> close</div>
      </div>
      <div class="bm-tabs"></div>
      <div class="bm-main"></div>
      <div class="craft-pager"></div>
      ${salvage}`;
    tabsEl = root.querySelector<HTMLDivElement>(".bm-tabs")!;
    mainEl = root.querySelector<HTMLDivElement>(".bm-main")!;
    pagerEl = root.querySelector<HTMLDivElement>(".craft-pager")!;
    // Scrolling the grid would leave the tooltip stranded over the wrong tile.
    mainEl.addEventListener("scroll", hideTip);
    const salvageBtn = root.querySelector<HTMLButtonElement>(".craft-salvage");
    salvageBtn?.addEventListener("click", () => {
      if (ctx.buildingId) send("salvage", { id: ctx.buildingId });
      close();
    });
    tabs.forEach((tab, i) => {
      const btn = document.createElement("div");
      btn.className = "bm-tab";
      btn.textContent = tab.label;
      btn.addEventListener("click", () => setTab(i));
      tabsEl.appendChild(btn);
    });
    if (tabs.length === 0) {
      mainEl.innerHTML = `<div class="craft-shelf__empty">No recipes available at this ${stationName}.</div>`;
      return;
    }
    renderTab();
  }

  function open(buildingType: BuildingType, openCtx: CraftCtx = {}): void {
    station = buildingType;
    ctx = openCtx;
    opened = true;
    activeTab = 0;
    tabs = buildTabs(buildingType);
    root.style.display = "flex";
    render();
    positionAboveHud();
  }
  function close(): void {
    opened = false;
    station = null;
    ctx = {};
    tabs = [];
    tiles = [];
    activeCustomRefresh = null;
    hideTip();
    root.style.display = "none";
    root.innerHTML = "";
  }
  function isOpen(): boolean { return opened; }
  function refresh(cube: Cube): void {
    lastCube = cube;
    if (!opened) return;
    applyAffordability();
    activeCustomRefresh?.(cube);
  }

  return { open, close, isOpen, refresh, cycleTab, selectHotkey };
}

/**
 * Find the nearest friendly, ready Forge/Workbench/Campfire within `radius` of
 * the cube. Returns the building's type (the caller passes it to
 * craftMenu.open). Campfire opens the same menu filtered to Cooking recipes —
 * the server routes those through the prep timer (craft-prep.ts).
 *
 * Exported here so main.ts's E-key handler can reuse the same proximity
 * check the server uses without duplicating logic. INTERACT_RADIUS=4 matches
 * server's items/craft.ts constant.
 */
export const INTERACT_RADIUS = 4;

export function findNearbyCraftBuilding(
  cube: Cube,
  buildings: Iterable<{ id: string; buildingType: string; teamId: number; state: string; x: number; z: number }>,
  radius: number = INTERACT_RADIUS,
): { id: string; type: BuildingType } | null {
  let best: { id: string; type: BuildingType; dist: number } | null = null;
  for (const b of buildings) {
    if (b.teamId !== cube.teamId) continue;
    if (b.state !== "ready") continue;
    if (b.buildingType !== "Forge" && b.buildingType !== "Workbench" && b.buildingType !== "Campfire" && b.buildingType !== "HuntersLodge") continue;
    const d = Math.hypot(b.x - cube.x, b.z - cube.z);
    if (d > radius) continue;
    if (!best || d < best.dist) {
      best = { id: b.id, type: b.buildingType as BuildingType, dist: d };
    }
  }
  if (!best) return null;
  return { id: best.id, type: best.type };
}
